import React, { useMemo } from 'react'
import { colors } from '../consts'
import { Doughnut } from 'react-chartjs-2'
import { DonutChartData } from '../../Utils'
import { Desc } from '../Typography/Text.styles'
import { DoughnutCon, DoughnutOuterCon, InnerDataCon } from './styles'
import './ChartSetup'
import type { ChartData, TooltipItem } from 'chart.js'

type DoughnutChartState = {
  data: DonutChartData[]
  title?: string
  height?: number
  cutout?: number | string
}

const DoughnutChart: React.FC<DoughnutChartState> = ({
  data,
  title,
  height,
  cutout = '50%',
}) => {
  const totalVotes = useMemo(() => data.reduce((acc, d) => acc + d.metric, 0), [data])
  const chartData = useMemo<ChartData<'doughnut'>>(
    () => ({
      labels: data.map((d) => d.label),
      datasets: [
        {
          data: data.map((d) => d.metric),
          backgroundColor: data.map((d) => d.color),
          label: '',
          borderWidth: 0,
        },
      ],
    }),
    [data]
  )
  const reversedData = useMemo(() => [...data].reverse(), [data])
  const getPercentLabel = (value: any, shouldParse: boolean = true): string => {
    const parsedValue = shouldParse ? value.parsed : value
    const percent = (parsedValue / totalVotes) * 100
    return `${percent.toFixed(1)}%`
  }

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      cutout: cutout,
      plugins: {
        legend: {
          display: false,
          position: 'bottom' as const,
        },
        tooltip: {
          position: 'nearest' as const,
          callbacks: {
            label: function (tooltipItem: TooltipItem<'doughnut'>) {
              return getPercentLabel(tooltipItem)
            },
            labelColor: function (context: any) {
              const color = context.dataset.backgroundColor
              const index = context.dataIndex
              return {
                borderColor: Array.isArray(color) ? color[index] : color,
                backgroundColor: Array.isArray(color) ? color[index] : color,
              }
            },
          },
        },
        title: {
          display: false,
          text: title,
        },
      },
    }),
    [cutout, getPercentLabel, title]
  )

  return (
    <DoughnutOuterCon height={height}>
      {data.length > 0 && (
        <>
          {title && (
            <Desc textColor={colors.gray.gray20} vertMargins={2}>
              {title}
            </Desc>
          )}
          <DoughnutCon>
            <Doughnut data={chartData} options={chartOptions} />
            <InnerDataCon>
              {reversedData.map((d, i) => (
                <div className="dataItem" key={d.label}>
                  <p className="metric">{getPercentLabel(d.metric, false)}</p>
                  <p className="title">{d.label}</p>
                </div>
              ))}
            </InnerDataCon>
          </DoughnutCon>
        </>
      )}
    </DoughnutOuterCon>
  )
}

export default DoughnutChart